import { apiSlice } from './apiSlice'

/**
 * AI module endpoints. Streaming chat lives outside RTK Query
 * (services/aiChatClient) — see the aiReportSlice precedent.
 * Consumers unwrap the {success, message, data} envelope themselves.
 */
const aiApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAiConversations: builder.query<any, void>({
      query: () => ({ url: '/api/ai/conversations' }),
      providesTags: ['AiConversation'],
    }),
    getAiConversation: builder.query<any, string | number>({
      query: (id) => ({ url: `/api/ai/conversations/${id}` }),
      providesTags: ['AiConversation'],
    }),
    deleteAiConversation: builder.mutation<any, string | number>({
      query: (id) => ({ url: `/api/ai/conversations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AiConversation'],
    }),
    getAiUsage: builder.query<any, void>({
      query: () => ({ url: '/api/ai/usage' }),
      providesTags: ['AiUsage'],
    }),
    getAiPendingApprovals: builder.query<any, void>({
      query: () => ({ url: '/api/ai/pending-approvals' }),
      providesTags: ['AiApproval'],
    }),
    approveAiTool: builder.mutation<any, number>({
      query: (approvalId) => ({
        url: '/api/ai/tools/approve',
        method: 'POST',
        data: { approval_id: approvalId },
      }),
      invalidatesTags: ['AiApproval', 'AiConversation'],
    }),
    rejectAiTool: builder.mutation<any, number>({
      query: (approvalId) => ({
        url: '/api/ai/tools/reject',
        method: 'POST',
        data: { approval_id: approvalId },
      }),
      invalidatesTags: ['AiApproval', 'AiConversation'],
    }),
    scanAiReceipt: builder.mutation<any, File>({
      query: (image) => {
        // Guard: an empty file appended a zero-byte 'image' part, which the
        // OCR backend rejected with an opaque 422 instead of a clear failure.
        if (!(image instanceof Blob) || image.size === 0) {
          throw new Error('scanAiReceipt requires a non-empty image file')
        }
        const formData = new FormData()
        formData.append('image', image)
        return { url: '/api/ai/scan/receipt', method: 'POST', data: formData }
      },
    }),
    parseAiPosSentence: builder.mutation<any, string>({
      query: (sentence) => ({
        url: '/api/ai/pos/parse',
        method: 'POST',
        data: { sentence },
      }),
    }),
    getAiDashboardInsights: builder.query<{ success?: boolean; message?: string; data?: unknown }, void>({
      query: () => ({ url: '/api/ai/insights/dashboard', optionalService: true }),
    }),
    getAiInventoryInsights: builder.query<any, void>({
      query: () => ({ url: '/api/ai/insights/inventory', optionalService: true }),
    }),
    askAiAccounting: builder.mutation<any, string>({
      // Backend route is GET ai/insights/accounting with a required ?question=
      query: (question) => ({
        url: '/api/ai/insights/accounting',
        method: 'GET',
        params: { question },
      }),
    }),
    summarizeAiReport: builder.mutation<any, { rows: Record<string, unknown>[]; report_title?: string }>({
      query: (payload) => ({
        url: '/api/ai/report/summarize',
        method: 'POST',
        data: payload,
      }),
    }),
    searchAiProducts: builder.query<any, string>({
      query: (q) => ({ url: '/api/ai/search/products', params: { q } }),
    }),
    dispatchAiAnalysis: builder.mutation<any, string>({
      query: (topic) => ({
        url: '/api/ai/analyze',
        method: 'POST',
        data: { topic },
      }),
    }),
  }),
})

export const {
  useGetAiConversationsQuery,
  useGetAiConversationQuery,
  useDeleteAiConversationMutation,
  useGetAiUsageQuery,
  useGetAiPendingApprovalsQuery,
  useApproveAiToolMutation,
  useRejectAiToolMutation,
  useScanAiReceiptMutation,
  useParseAiPosSentenceMutation,
  useGetAiDashboardInsightsQuery,
  useGetAiInventoryInsightsQuery,
  useAskAiAccountingMutation,
  useSummarizeAiReportMutation,
  useSearchAiProductsQuery,
  useDispatchAiAnalysisMutation,
} = aiApiSlice
